import { useCallback, useState, type MouseEvent } from 'react';
import { Segmentado } from './Segmentado';
import { anio, diferenciaDias, formatearCorto, formatearCortoConAnio, sumarDias, type FechaLocal } from '@/domain/dates';
import {
  decimalesDePaso,
  ejeY,
  franjasEnPeriodo,
  puntosEnPeriodo,
  resumenDeSerie,
  type FranjaGrafica,
  type PuntoGrafica,
  type TonoFranja,
} from '@/domain/grafica';
import { fechaCorta } from '@/domain/hoy';
import { formatearNumero } from '@/domain/numbers';
import { RANGOS, inicioDeRango, type Rango } from '@/domain/tendencia';

/** Alto del dibujo en px; el ancho es el de la pantalla. */
const ALTO = 188;
const IZQUIERDA = 42;
const DERECHA = 10;
const ARRIBA = 10;
const ABAJO = 10;

const RELLENO_FRANJA: Record<TonoFranja, string> = {
  deficit: 'fill-deficit',
  mantenimiento: 'fill-mantenimiento',
  superavit: 'fill-superavit',
};
const MUESTRA_FRANJA: Record<TonoFranja, string> = {
  deficit: 'bg-deficit',
  mantenimiento: 'bg-mantenimiento',
  superavit: 'bg-superavit',
};

const PERIODO: Record<Rango, string> = {
  '1m': 'En el último mes',
  '3m': 'En los últimos 3 meses',
  '6m': 'En los últimos 6 meses',
  '1a': 'En el último año',
  todo: 'En todo tu historial',
};

/**
 * El ancho real del contenedor, para dibujar en px sin deformar los puntos.
 * Mide cuando el contenedor aparece: si el primer rango no tenía datos, el
 * contenedor no existía y antes ya no se medía, así que la gráfica quedaba en
 * blanco al elegir otro rango (revisión de la 0.4.0).
 */
function useAncho() {
  const [ancho, setAncho] = useState(0);
  const ref = useCallback((el: HTMLDivElement | null) => {
    if (el === null) return;
    const medir = () => setAncho(Math.floor(el.getBoundingClientRect().width));
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);
  return { ref, ancho };
}

interface Props {
  /** Para el selector de rango: "Periodo de la gráfica de peso". */
  etiqueta: string;
  puntos: readonly PuntoGrafica[];
  /** "Peso del día" */
  nombrePuntos: string;
  linea?: readonly PuntoGrafica[];
  /** "Tendencia de 7 días" */
  nombreLinea?: string;
  franjas?: readonly FranjaGrafica[];
  /** "70.0 kg" */
  formatear: (valor: number) => string;
  /** "−0.4 kg" */
  formatearCambio: (valor: number) => string;
  /** "12 días con peso" */
  textoCantidad: (n: number) => string;
  /** "kg", para la leyenda del eje. */
  unidad: string;
  /** Lo que se dice si el periodo no tiene datos. */
  vacio: string;
  hoy: FechaLocal;
  rangoInicial?: Rango;
}

/**
 * Gráfica de una serie por día (design.md, 12.4; Req. 8.9, 16.4 a 16.6): los
 * puntos, la línea, las franjas de fondo con su leyenda en texto, el eje con
 * marcas redondas y el selector de rango. Tocar el dibujo muestra el valor
 * del punto más cercano. El dibujo es solo visual: el resumen en texto y la
 * lista de valores dicen lo mismo para lectores de pantalla.
 */
export function GraficaSerie({
  etiqueta,
  puntos,
  nombrePuntos,
  linea = [],
  nombreLinea,
  franjas = [],
  formatear,
  formatearCambio,
  textoCantidad,
  unidad,
  vacio,
  hoy,
  rangoInicial = '3m',
}: Props) {
  const [rango, setRango] = useState<Rango>(rangoInicial);
  const [elegido, setElegido] = useState<FechaLocal | null>(null);
  const [listaAbierta, setListaAbierta] = useState(false);
  const { ref, ancho } = useAncho();

  const primerDato = puntos.reduce<FechaLocal | null>((min, p) => (min === null || p.fecha < min ? p.fecha : min), null);
  const desde = inicioDeRango(rango, hoy, primerDato);
  const hasta = hoy;
  const visibles = puntosEnPeriodo(puntos, desde, hasta);
  const lineaVisible = puntosEnPeriodo(linea, desde, hasta);
  const bandas = franjasEnPeriodo(franjas, desde, hasta);
  const resumen = resumenDeSerie(visibles);
  const lineaDe = new Map(lineaVisible.map((p) => [p.fecha, p.valor]));

  const selector = (
    <Segmentado
      etiqueta={etiqueta}
      valor={rango}
      opciones={RANGOS.map((r) => ({ valor: r.valor, etiqueta: r.corto, nombre: r.nombre }))}
      onCambio={(r) => {
        setRango(r);
        setElegido(null);
      }}
      completo
      compacto
    />
  );

  if (resumen === null) {
    return (
      <div className="space-y-3">
        {selector}
        <p className="rounded-panel bg-superficie p-5 text-[13px] leading-relaxed text-texto-suave">{vacio}</p>
      </div>
    );
  }

  // --- Escalas ---------------------------------------------------------------
  const eje = ejeY([...visibles.map((p) => p.valor), ...lineaVisible.map((p) => p.valor)]);
  const anchoUtil = Math.max(1, ancho - IZQUIERDA - DERECHA);
  const altoUtil = ALTO - ARRIBA - ABAJO;
  // Cada día es una franja del mismo ancho y su punto va al centro: con un
  // solo día, el punto queda en medio y no sobre el eje (revisión de la 0.4.0)
  const dias = diferenciaDias(desde, hasta) + 1;
  const inicioDelDia = (fecha: FechaLocal) => IZQUIERDA + (diferenciaDias(desde, fecha) / dias) * anchoUtil;
  const x = (fecha: FechaLocal) => inicioDelDia(fecha) + anchoUtil / dias / 2;
  const y = (valor: number) => ARRIBA + (1 - (valor - eje.desde) / (eje.hasta - eje.desde)) * altoUtil;
  const derecha = IZQUIERDA + anchoUtil;

  // La línea se corta donde falta un día (Req. 16.2: sin pesajes no hay tendencia)
  let trazo = '';
  lineaVisible.forEach((p, i) => {
    const anterior = lineaVisible[i - 1];
    const sigue = anterior !== undefined && diferenciaDias(anterior.fecha, p.fecha) === 1;
    trazo += `${sigue ? 'L' : 'M'}${x(p.fecha).toFixed(1)},${y(p.valor).toFixed(1)}`;
  });

  const punto = elegido === null ? undefined : visibles.find((p) => p.fecha === elegido);
  const cruzaAnio = anio(desde) !== anio(hasta);
  /** "14 oct", o "14 oct 2025" si el periodo cruza de año. */
  const fechaEje = (f: FechaLocal) => (cruzaAnio ? formatearCortoConAnio(f) : formatearCorto(f));
  /** "Mié, 14 oct", con el año si el periodo cruza de año. */
  const fechaDePunto = (f: FechaLocal) => (cruzaAnio ? `${fechaCorta(f)} ${anio(f)}` : fechaCorta(f));
  const decimales = decimalesDePaso(eje.paso);

  function tocar(evento: MouseEvent<SVGSVGElement>) {
    const caja = evento.currentTarget.getBoundingClientRect();
    const dx = evento.clientX - caja.left;
    let cercano: PuntoGrafica | null = null;
    for (const p of visibles) {
      if (cercano === null || Math.abs(x(p.fecha) - dx) < Math.abs(x(cercano.fecha) - dx)) cercano = p;
    }
    setElegido(cercano?.fecha ?? null);
  }

  // --- Resumen en texto (Req. 16.6) -------------------------------------------
  const lineaPrimera = lineaVisible[0];
  const lineaUltima = lineaVisible[lineaVisible.length - 1];
  const partes = [
    resumen.cantidad > 1
      ? `${PERIODO[rango]}: ${textoCantidad(resumen.cantidad)}, del ${fechaEje(resumen.primero.fecha)} al ${fechaEje(resumen.ultimo.fecha)}.`
      : `${PERIODO[rango]}: ${textoCantidad(1)}, el ${fechaEje(resumen.primero.fecha)}.`,
    resumen.cantidad > 1
      ? `Mínimo ${formatear(resumen.minimo.valor)} (${fechaEje(resumen.minimo.fecha)}) y máximo ${formatear(resumen.maximo.valor)} (${fechaEje(resumen.maximo.fecha)}).`
      : `${nombrePuntos}: ${formatear(resumen.primero.valor)}.`,
    nombreLinea !== undefined && lineaPrimera !== undefined && lineaUltima !== undefined && lineaPrimera !== lineaUltima
      ? `${nombreLinea}: de ${formatear(lineaPrimera.valor)} a ${formatear(lineaUltima.valor)} (${formatearCambio(lineaUltima.valor - lineaPrimera.valor)}).`
      : resumen.cantidad > 1
        ? `Del primero al último: ${formatearCambio(resumen.ultimo.valor - resumen.primero.valor)}.`
        : null,
    bandas.length > 0
      ? `Etapas del plan: ${bandas.map((b) => `${b.etiqueta.toLocaleLowerCase('es-MX')} del ${fechaEje(b.desde)} al ${fechaEje(b.hasta)}`).join('; ')}.`
      : null,
  ].filter((p) => p !== null);
  const tonos = [...new Map(bandas.map((b) => [b.tono, b.etiqueta])).entries()];

  return (
    <figure className="space-y-3">
      {selector}

      <div className="rounded-panel bg-superficie px-3 pb-3 pt-2">
        {/* El valor del punto tocado. El dibujo es solo visual: para lectores de pantalla están el resumen y la lista */}
        <p className="min-h-6 px-1 text-[12px] tabular-nums text-texto-suave" aria-live="polite">
          {punto === undefined ? (
            <span aria-hidden="true">Toca la gráfica para ver un valor.</span>
          ) : (
            [
                fechaDePunto(punto.fecha),
                formatear(punto.valor),
                nombreLinea !== undefined && lineaDe.has(punto.fecha)
                  ? `${nombreLinea.toLocaleLowerCase('es-MX')} ${formatear(lineaDe.get(punto.fecha) ?? 0)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
          )}
        </p>

        <div ref={ref} className="relative" style={{ height: ALTO }}>
          {ancho > 0 && (
            <>
              <svg
                width={ancho}
                height={ALTO}
                viewBox={`0 0 ${ancho} ${ALTO}`}
                aria-hidden="true"
                focusable="false"
                className="block touch-manipulation"
                onClick={tocar}
              >
                {bandas.map((b) => {
                  // Una franja cubre sus días completos
                  const inicio = inicioDelDia(b.desde);
                  const fin = Math.min(derecha, inicioDelDia(sumarDias(b.hasta, 1)));
                  return (
                    <rect
                      key={`${b.tono}-${b.desde}`}
                      x={inicio}
                      y={ARRIBA}
                      width={Math.max(2, fin - inicio)}
                      height={altoUtil}
                      className={RELLENO_FRANJA[b.tono]}
                      fillOpacity={0.16}
                    />
                  );
                })}
                {eje.marcas.map((m) => (
                  <line
                    key={m}
                    x1={IZQUIERDA}
                    x2={derecha}
                    y1={y(m)}
                    y2={y(m)}
                    className="stroke-borde"
                    strokeWidth={1}
                  />
                ))}
                {trazo !== '' && (
                  <path d={trazo} fill="none" className="stroke-marca" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
                )}
                {visibles.map((p) => (
                  <circle
                    key={p.fecha}
                    cx={x(p.fecha)}
                    cy={y(p.valor)}
                    r={3.5}
                    className="fill-superficie stroke-texto-suave"
                    strokeWidth={1.5}
                  />
                ))}
                {punto !== undefined && (
                  <>
                    <line x1={x(punto.fecha)} x2={x(punto.fecha)} y1={ARRIBA} y2={ARRIBA + altoUtil} className="stroke-texto-suave" strokeDasharray="3 3" />
                    <circle cx={x(punto.fecha)} cy={y(punto.valor)} r={6} className="fill-marca stroke-superficie" strokeWidth={2} />
                  </>
                )}
              </svg>
              {/* Marcas del eje en texto normal: se leen igual a 320 px y pasan la revisión de contraste */}
              {eje.marcas.map((m) => (
                <span
                  key={m}
                  aria-hidden="true"
                  className="absolute left-0 w-[36px] text-right text-[11px] leading-none tabular-nums text-texto-suave"
                  style={{ top: y(m) - 5 }}
                >
                  {formatearNumero(m, { decimales })}
                </span>
              ))}
            </>
          )}
        </div>
        {/* Con un solo día, su fecha va al centro, bajo su punto */}
        <div
          aria-hidden="true"
          className={`flex pl-[42px] pr-1 pt-1 text-[11px] text-texto-suave ${desde === hasta ? 'justify-center' : 'justify-between'}`}
        >
          <span>{fechaEje(desde)}</span>
          {desde !== hasta && <span>{fechaEje(hasta)}</span>}
        </div>
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 px-1 text-[12px] text-texto-suave">
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block size-2.5 rounded-full border-[1.5px] border-texto-suave bg-superficie" />
          <span>{nombrePuntos}</span>
        </li>
        {nombreLinea !== undefined && lineaVisible.length > 0 && (
          <li className="flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-[3px] w-4 rounded-full bg-marca" />
            <span>{nombreLinea}</span>
          </li>
        )}
        {tonos.map(([tono, nombre]) => (
          <li key={tono} className="flex items-center gap-1.5">
            <span aria-hidden="true" className={`inline-block size-3 rounded-[3px] opacity-40 ${MUESTRA_FRANJA[tono]}`} />
            <span>{nombre}</span>
          </li>
        ))}
        <li>
          <span>En {unidad}</span>
        </li>
      </ul>

      <figcaption className="px-1 text-[13px] leading-relaxed text-texto-suave">{partes.join(' ')}</figcaption>

      {/* La lista se arma solo abierta: con un año de datos serían cientos de renglones ocultos */}
      <details className="px-1" open={listaAbierta} onToggle={(e) => setListaAbierta(e.currentTarget.open)}>
        <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-marca">
          Ver los valores ({resumen.cantidad})
        </summary>
        {listaAbierta && (
        <ul className="divide-y divide-borde">
          {[...visibles].reverse().map((p) => (
            <li key={p.fecha} className="flex items-baseline justify-between gap-3 py-2 text-[13px]">
              <span className="text-texto-suave">{fechaDePunto(p.fecha)}</span>
              <span className="text-right tabular-nums text-texto">
                {formatear(p.valor)}
                {nombreLinea !== undefined && lineaDe.has(p.fecha) && (
                  <span className="text-texto-suave">
                    {' '}
                    · {nombreLinea.toLocaleLowerCase('es-MX')} {formatear(lineaDe.get(p.fecha) ?? 0)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        )}
      </details>
    </figure>
  );
}
